import { useEffect, useState } from 'react';
import { api } from '@/services/api';
import type { Audience, Category, MeasurementRange, SizeRow } from '@/types/domain';

interface Props {
  brandSlug: string;
  brandName: string;
  audience: Audience;
  category: Category;
  onClose: () => void;
}

const COLUMNS: Array<{ key: keyof SizeRow; label: string }> = [
  { key: 'chest', label: 'Chest / bust' },
  { key: 'waist', label: 'Waist' },
  { key: 'hip', label: 'Hip' },
  { key: 'shoulder', label: 'Shoulder' },
  { key: 'sleeve', label: 'Sleeve' },
  { key: 'thigh', label: 'Thigh' },
  { key: 'inseam', label: 'Inseam' },
];

function formatRange(range: MeasurementRange): string {
  if (!range) return '—';
  const [min, max] = range;
  return min === max ? `${min} cm` : `${min}–${max} cm`;
}

/**
 * The real size chart behind the "Size Guide" link — reads directly from
 * the same /api/charts endpoint (and therefore the same verified data) the
 * comparison engine itself uses. No separate/duplicated chart data.
 */
export function SizeGuideDrawer({ brandSlug, brandName, audience, category, onClose }: Props) {
  const [status, setStatus] = useState<'loading' | 'error' | 'success'>('loading');
  const [rows, setRows] = useState<SizeRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    api.getChart(brandSlug, audience, category).then((res) => {
      if (cancelled) return;
      if (!res.success) {
        setStatus('error');
        return;
      }
      const data = res.data as { rows: SizeRow[] };
      setRows(data.rows);
      setStatus('success');
    });
    return () => {
      cancelled = true;
    };
  }, [brandSlug, audience, category]);

  const visibleColumns = COLUMNS.filter((col) => rows.some((r) => r[col.key] !== null));

  return (
    <div className="fixed inset-0 z-30 flex justify-end bg-ink/40" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${brandName} size guide`}
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-md overflow-y-auto bg-white p-5 shadow-card"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted">Size guide</p>
            <p className="text-lg font-bold text-ink">{brandName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close size guide"
            className="min-h-[36px] min-w-[36px] rounded-full border border-stone-200 text-muted hover:text-ink"
          >
            ✕
          </button>
        </div>

        {status === 'loading' && <p className="text-sm text-muted">Loading size guide…</p>}
        {status === 'error' && (
          <p role="alert" className="rounded-xl bg-danger/10 p-3 text-sm text-danger">
            Couldn't load this brand's size guide right now.
          </p>
        )}
        {status === 'success' && rows.length === 0 && (
          <p className="text-sm text-muted">No published measurements are available for this brand/category yet.</p>
        )}
        {status === 'success' && rows.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-stone-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-stone-50 text-xs uppercase text-muted">
                <tr>
                  <th className="px-3 py-2">Size</th>
                  {visibleColumns.map((col) => (
                    <th key={col.key} className="px-3 py-2">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-3 py-2 font-medium text-ink">{row.label}</td>
                    {visibleColumns.map((col) => (
                      <td key={col.key} className="px-3 py-2 text-muted">
                        {formatRange(row[col.key] as MeasurementRange)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-4 text-xs text-muted">
          Measurements shown in centimetres, as published by {brandName}'s official size chart.
        </p>
      </div>
    </div>
  );
}
